// RapportsPage.jsx
import React, { useState } from "react";
import { BiSearch, BiX, BiDownload, BiFilter } from "react-icons/bi";
import SalesReport from "./reports/SalesReport";
import FinancialReport from "./reports/FinancialReport";
import ClientsReport from "./reports/ClientsReport";
import AppointmentsReport from "./reports/AppointmentsReport";
import CommissionsReport from "./reports/CommissionsReport";
import GiftCardsReport from "./reports/GiftCardsReport";
import SubscriptionsReport from "./reports/SubscriptionsReport";

// Configuration des rapports avec leurs composants
const reportsConfig = [
  {
    id: "sales",
    name: "Rapport des ventes",
    description:
      "Quantité et montant des ventes, à l'exclusion des ventes de cartes cadeaux.",
    icon: "📊",
    component: <SalesReport />,
    count: 4,
    color: "bg-blue-50 text-blue-600",
  },
  {
    id: "financial",
    name: "Rapport financier",
    description: "Récapitulatif complet des ventes, paiements et passifs.",
    icon: "💰",
    component: <FinancialReport />,
    count: 2,
    color: "bg-green-50 text-green-600",
  },
  {
    id: "clients",
    name: "Rapport clients",
    description: "Liste complète de tous les clients actifs avec statistiques.",
    icon: "👥",
    component: <ClientsReport />,
    count: 1,
    color: "bg-purple-50 text-purple-600",
  },
  {
    id: "appointments",
    name: "Rapport des rendez-vous",
    description:
      "Aperçu général des habitudes et des tendances en matière de rendez-vous.",
    icon: "📅",
    component: <AppointmentsReport />,
    count: 4,
    color: "bg-orange-50 text-orange-600",
  },
  {
    id: "commissions",
    name: "Rapport des commissions",
    description: "Aperçu des commissions gagnées par les membres de l'équipe.",
    icon: "💸",
    component: <CommissionsReport />,
    count: 3,
    color: "bg-red-50 text-red-600",
  },
  {
    id: "giftcards",
    name: "Rapport des cartes cadeaux",
    description: "Liste complète des cartes cadeaux émises et non utilisées.",
    icon: "🎁",
    component: <GiftCardsReport />,
    count: 3,
    color: "bg-pink-50 text-pink-600",
  },
  {
    id: "subscriptions",
    name: "Rapport des abonnements",
    description: "Liste complète des abonnements actifs et analyse.",
    icon: "🔄",
    component: <SubscriptionsReport />,
    count: 3,
    color: "bg-indigo-50 text-indigo-600",
  },
];

const RapportsPage = () => {
  const [search, setSearch] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);

  // Filtrer les rapports
  const filteredReports = reportsConfig.filter(
    (report) =>
      !search ||
      report.name.toLowerCase().includes(search.toLowerCase()) ||
      report.description.toLowerCase().includes(search.toLowerCase()),
  );

  // Retour à la liste des rapports
  const handleBack = () => {
    setSelectedReport(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* En-tête */}
        <div className="bg-white border-b border-gray-200 px-6 py-6">
          <div className="flex flex-col lg:flex-row gap-4 w-full lg:justify-between lg:items-start mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                {selectedReport && (
                  <button
                    onClick={handleBack}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    ←
                  </button>
                )}
                <h1 className="font-poppins font-semibold text-[24px] text-title">
                  {selectedReport
                    ? selectedReport.name
                    : "Rapports et analyses"}
                </h1>
              </div>
              <p className="font-nunito text-[14px] text-text">
                {selectedReport
                  ? selectedReport.description
                  : "Accédez à tous vos rapports et analyses détaillées."}
              </p>
            </div>
          </div>

          {/* Barre de recherche - visible seulement sur la liste */}
          {!selectedReport && (
            <div className="relative mt-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <BiSearch size={20} className="text-gray-400" />
              </div>

              <input
                type="text"
                placeholder="Recherchez par nom ou description de rapport"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-10 py-3 border border-gray-300 font-nunito text-[14px] text-text focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent rounded-lg"
              />

              {search && (
                <button
                  className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-100 px-2"
                  onClick={() => setSearch("")}
                >
                  <BiX size={18} className="text-gray-400" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Contenu principal */}
        <div className="p-6">
          {selectedReport ? (
            // Afficher le rapport sélectionné
            <div className="bg-white rounded-lg shadow-sm">
              {selectedReport.component}
            </div>
          ) : (
            // Grille des rapports disponibles
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredReports.map((report) => (
                <div
                  key={report.id}
                  onClick={() => setSelectedReport(report)}
                  className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer border border-gray-100 hover:border-secondary-200"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div
                        className={`w-12 h-12 rounded-lg ${report.color} flex items-center justify-center text-2xl`}
                      >
                        {report.icon}
                      </div>
                      <span className="bg-gray-100 text-gray-600 text-sm px-2 py-1 rounded">
                        {report.count} rapports
                      </span>
                    </div>

                    <h3 className="font-poppins font-semibold text-[16px] text-title mb-2">
                      {report.name}
                    </h3>

                    <p className="font-nunito text-[14px] text-text line-clamp-2">
                      {report.description}
                    </p>

                    <div className="mt-4 flex items-center text-secondary-600 text-sm font-medium">
                      <span>Voir le rapport</span>
                      <span className="ml-2">→</span>
                    </div>
                  </div>
                </div>
              ))}

              {filteredReports.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <div className="text-6xl mb-4">🔍</div>
                  <h3 className="font-poppins font-semibold text-[18px] text-title mb-2">
                    Aucun rapport trouvé
                  </h3>
                  <p className="font-nunito text-[14px] text-text">
                    Aucun rapport ne correspond à votre recherche.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RapportsPage;

// // RapportsPage.jsx
// import React, { useState } from "react";
// import { BiSearch, BiX } from "react-icons/bi";

// // Configuration des rapports
// const reportsConfig = [
//   {
//     id: "sales",
//     name: "Rapport des ventes",
//     description:
//       "Quantité et montant des ventes, à l'exclusion des ventes de cartes cadeaux.",
//     icon: "sales",
//     component: <span>Rappor des ventes</span>,
//     count: 4,
//   },
//   {
//     id: "financial",
//     name: "Rapport financier",
//     description: "Récapitulatif complet des ventes, paiements et passifs.",
//     icon: "financial",
//     component: <span>Rappor des finances</span>,
//     count: 2,
//   },
//   {
//     id: "clients",
//     name: "Rapport clients",
//     description: "Liste complète de tous les clients actifs avec statistiques.",
//     icon: "clients",
//     component: <span>Rappor des clients</span>,
//     count: 1,
//   },
//   {
//     id: "appointments",
//     name: "Rapport des rendez-vous",
//     description:
//       "Aperçu général des habitudes et des tendances en matière de rendez-vous.",
//     icon: "appointments",
//     component: <span>Rappor des rendez-vous</span>,
//     count: 4,
//   },
//   {
//     id: "commissions",
//     name: "Rapport des commissions",
//     description: "Aperçu des commissions gagnées par les membres de l'équipe.",
//     icon: "commissions",
//     component: <span>Rappor des commissions</span>,
//     count: 3,
//   },
//   {
//     id: "giftcards",
//     name: "Rapport des cartes cadeaux",
//     description: "Liste complète des cartes cadeaux émises et non utilisées.",
//     icon: "giftcards",
//     component: <span>Rappor des cartes cadeaux</span>,
//     count: 3,
//   },
//   {
//     id: "subscriptions",
//     name: "Rapport des abonnements",
//     description: "Liste complète des abonnements actifs et analyse.",
//     icon: "subscriptions",
//     component: <span>Rappor des abonnements</span>,
//     count: 3,
//   },
// ];

// const RapportsPage = () => {
//   const [search, setSearch] = useState("");
//   const [selectedReport, setSelectedReport] = useState(null);

//   // Filtrer les rapports
//   const filteredReports = reportsConfig.filter(
//     (report) =>
//       !search ||
//       report.name.toLowerCase().includes(search.toLowerCase()) ||
//       report.description.toLowerCase().includes(search.toLowerCase()),
//   );

//   return (
//     <div className="min-h-screen bg-gray-50">
//       <div className="max-w-7xl mx-auto">
//         {/* En-tête */}
//         <div className="bg-white border-b border-gray-200 px-6 py-6">
//           <div className="flex flex-col lg:flex-row gap-4 w-full lg:justify-between lg:items-start mb-4">
//             <div className="flex-1">
//               <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2">
//                 <h1 className="font-poppins font-semibold text-[18px] text-title">
//                   Rapports et analyses
//                 </h1>
//               </div>
//               <p className="font-nunito text-[14px] text-text">
//                 Accédez à tous vos rapports.
//               </p>
//             </div>
//           </div>

//           {/* Barre de recherche */}
//           <div className="relative mt-4">
//             <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
//               <BiSearch size={20} className="text-gray-400" />
//             </div>

//             <input
//               type="text"
//               placeholder="Recherchez par nom ou description de rapport"
//               value={search}
//               onChange={(e) => setSearch(e.target.value)}
//               className="w-full pl-10 pr-10 py-2.5 border border-gray-300 font-nunito text-[14px] text-text focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
//             />

//             {search && (
//               <button
//                 className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-100 px-2"
//                 onClick={() => setSearch("")}
//               >
//                 <BiX size={18} className="text-gray-400" />
//               </button>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default RapportsPage;
